<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useStore } from 'vuex';
import getAccountCurrentPositionsV2 from '@gpx-spa/queries/src/getAccountCurrentPositionsV2';
import getAccountShipmentExceptions from '@gpx-spa/queries/src/getAccountShipmentExceptions';
import type { V2ShipmentDetail } from '@gpx-spa/queries/src/getAccountShipmentV2';
import ShipmentDetailTabs from '@/components/shipment/detail/ShipmentDetailTabs.vue';
import ShipmentDetailOverviewTab from '@/components/shipment/detail/tabs/ShipmentDetailOverviewTab.vue';
import ShipmentDetailExceptionsTab from '@/components/shipment/detail/tabs/ShipmentDetailExceptionsTab.vue';
import ShipmentDetailStopsTab from '@/components/shipment/detail/tabs/ShipmentDetailStopsTab.vue';
import {
  allDeviceIds,
  allShipmentDevices,
} from '@/utils/shipment-detail-stops';
import { stopsForShipment } from '@/utils/shipment-detail-placeholder-data';
import { shipmentDetailColumnStyle } from '@/components/shipment/detail/shipment-detail-shell';
import * as events from '@/events';
import { setTitle } from '@/utils/title';

const props = defineProps<{
  shipment: V2ShipmentDetail | null | undefined;
}>();

const store = useStore();
const route = useRoute();
const accountId = computed(() => store.getters['auth/accountId'] as number);
const shipmentId = computed(() => Number(route.params.id) || 0);
const activeTab = ref('overview');

const deviceIds = computed(() => allDeviceIds(props.shipment));
const shipmentDevices = computed(() => allShipmentDevices(props.shipment));

const positionsQuery = getAccountCurrentPositionsV2(accountId, deviceIds, {
  enabled: computed(() => deviceIds.value.length > 0),
});

const devicePositions = computed(() => positionsQuery.data.value ?? []);

const exceptionsQuery = getAccountShipmentExceptions(accountId, shipmentId);

const exceptionRows = computed(() => exceptionsQuery.data.value ?? []);
const openExceptions = computed(() =>
  exceptionRows.value.filter((e) => e.status === 'open'),
);
const stopItems = computed(() => stopsForShipment(props.shipment));

const panelStyle = {
  ...shipmentDetailColumnStyle,
  height: '100%',
  minHeight: '100%',
};

function switchToExceptions() {
  activeTab.value = 'exceptions';
}

watch(
  () => props.shipment,
  (val) => {
    if (!val) return;
    const name = val.name?.trim();
    setTitle(name ? `Shipment ${name}` : `Shipment #${shipmentId.value}`);
    events.trigger(events.names.SHIPMENT_VIEWED, val);
  },
  { immediate: true },
);

onMounted(() => {
  nextTick(() => {
    if (props.shipment) {
      events.trigger(events.names.SHIPMENT_VIEWED, props.shipment);
    }
  });
});
</script>

<template>
  <div class="shipment-detail-panel" :style="panelStyle">
    <div
      v-if="!shipment"
      class="flex justify-center py-12 text-sm text-neutral-500"
    >
      Loading shipment…
    </div>
    <ShipmentDetailTabs
      v-else
      v-model="activeTab"
      class="flex min-h-0 flex-1 flex-col"
      :stops-count="stopItems.length"
      :exceptions-count="openExceptions.length"
    >
      <template #overview>
        <ShipmentDetailOverviewTab
          :shipment="shipment"
          :exceptions="openExceptions"
          :device-positions="devicePositions"
          :positions-loading="positionsQuery.isPending.value"
          @view-exceptions="switchToExceptions"
        />
      </template>
      <template #stops>
        <ShipmentDetailStopsTab :stops="stopItems" />
      </template>
      <template #exceptions>
        <ShipmentDetailExceptionsTab
          :exceptions="exceptionRows"
          :devices="shipmentDevices"
          :is-loading="exceptionsQuery.isPending.value"
          :is-error="exceptionsQuery.isError.value"
        />
      </template>
    </ShipmentDetailTabs>
  </div>
</template>

<style scoped>
.shipment-detail-panel {
  display: flex;
  width: 100%;
  min-height: 100%;
  flex-direction: column;
  overflow: hidden;
}

.shipment-detail-panel :deep(.p-tabs),
.shipment-detail-panel :deep(.p-tabpanels),
.shipment-detail-panel :deep(.p-tabpanel) {
  background-color: transparent !important;
}

.shipment-detail-panel :deep(.p-tabpanels) {
  flex: 1 1 auto;
  min-height: 0;
}
</style>
